/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import {
  Alert, AlertTitle, Box, Typography,
} from '@mui/material';
import {
  TravelerProfileSuggestions,
  TravelerProfileSuggestionsValidationIssue,
  TravelerProfileSuggestionsValidationSeverity,
  TravelerProfileSuggestionsValidationStep,
} from '@marageti/z4-sdk/lib/people';
import { useTravelerProfileSuggestionsStore } from '../../store';

type SectionKey = keyof TravelerProfileSuggestions;

const SECTION_LABELS: Record<SectionKey, string> = {
  travelPreferences: 'Travel Preferences',
  significantDates: 'Significant Dates',
  loyaltyPrograms: 'Loyalty Programs',
  giftsReceived: 'Gifts Received',
  householdDetails: 'Household Details',
};

const sectionLabel = (key: string): string => (
  (SECTION_LABELS as Record<string, string | undefined>)[key] ?? key
);

type SurfacedSeverity = 'info' | 'warning' | 'error';

/**
 * Maps the MCP validation severity vocabulary onto the subset of MUI Alert
 * severities we surface. `'none'` is the validator's "no concerns" signal and
 * stays hidden; everything above it (info/warning/error) renders.
 */
const toAlertSeverity = (
  severity: TravelerProfileSuggestionsValidationSeverity,
): SurfacedSeverity | null => {
  switch (severity) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    case 'none':
    default:
      return null;
  }
};

const ALERT_COPY: Record<SurfacedSeverity, { title: string }> = {
  error: {
    title: 'Address flagged issues before importing',
  },
  warning: {
    title: 'Review AI-generated data before applying',
  },
  info: {
    title: 'Notes about the AI-generated data',
  },
};

type FlatIssue = TravelerProfileSuggestionsValidationIssue & {
  sectionKey?: string;
};

const collectIssues = (
  validation: TravelerProfileSuggestionsValidationStep,
): FlatIssue[] => {
  const out: FlatIssue[] = validation.globalIssues.map((issue) => ({ ...issue }));
  Object.entries(validation.sectionResults).forEach(([section, result]) => {
    if (!result) return;
    result.issues.forEach((issue) => {
      out.push({ ...issue, sectionKey: section });
    });
  });
  return out;
};

/**
 * Inline banner that summarises the validator's verdict on the current set
 * of AI suggestions. Renders only while the user is in the review flow and
 * the validation step actually has something to say (severity above `'none'`
 * or at least one issue present). Otherwise it stays out of the way.
 *
 * Place this inside the page layout where the suggestions cards render — the
 * component reads its data from the suggestions context, so it just needs to
 * be a descendant of `TravelerProfileSuggestionsProvider`.
 */
const SuggestionValidationAlert = () => {
  const ctx = useTravelerProfileSuggestionsStore();

  // Computed before any early-return so hook order stays stable across
  // renders regardless of whether the suggestions provider is mounted.
  const issues = useMemo<FlatIssue[]>(
    () => (ctx?.validation ? collectIssues(ctx.validation) : []),
    [ctx?.validation],
  );

  if (!ctx) return null;

  const { isReviewing, validation } = ctx;
  if (!isReviewing || !validation) return null;

  const hasIssues = issues.length > 0;
  const isElevatedSeverity = validation.severity !== 'none';
  if (!hasIssues && !isElevatedSeverity) return null;

  const muiSeverity = toAlertSeverity(validation.severity);
  if (!muiSeverity) return null;

  const copy = ALERT_COPY[muiSeverity];

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity={muiSeverity}>
        <AlertTitle>{copy.title}</AlertTitle>
        {validation.summary && (
          <Typography variant="bodyBold" component="p" sx={{ mt: 0.5 }}>
            {validation.summary}
          </Typography>
        )}
        {hasIssues && (
          <Box component="ul" sx={{ pl: 2.5, m: 0, mt: 0.5 }}>
            {issues.map((issue, idx) => (
              // Issues don't carry a stable id; combine sectionKey + jsonPath
              // + index for a deterministic key within a single render.
              <li key={`${issue.sectionKey ?? 'global'}-${issue.jsonPath}-${idx}`}>
                <Typography variant="body" component="span">
                  {issue.sectionKey && (
                    <Box component="strong" sx={{ mr: 0.5 }}>
                      {`${sectionLabel(issue.sectionKey)}:`}
                    </Box>
                  )}
                  {issue.problem}
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Alert>
    </Box>
  );
};

export default SuggestionValidationAlert;
